import { useState } from "react";
import { Loader2, Download, FileText } from "lucide-react";

import Loading from "../components/common/LoaderCommon";
import TableComponent from "../components/common/TableComponent";
import PaginationComponent from "../components/PaginationComponent";
import { useGetAllConsolidateSalesAndReturnReport } from "../service/customerService";
import axiosInstance from "../service/axios";

type Props = {
  customerId: string | undefined;
};

function CustomerReportConsolidatedReport({ customerId }: Props) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: reportData, isLoading } =
    useGetAllConsolidateSalesAndReturnReport({
      pageNo: currentPage,
      pageSize,
      customerId: Number(customerId),
    });

  const totalPages = Math.ceil((reportData?.totalElements || 0) / pageSize);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await axiosInstance.post(
        "/reports/getConsolidateSalesAndReturnReport",
        { customerId: Number(customerId), download: true },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "consolidated-report.pdf";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setIsDownloading(false);
    }
  };

  const customerReportColumns = [
    { header: "Customer ID", accessor: "customerId" },
    { header: "Invoice Number", accessor: "invoiceNumber" },
    { header: "Date", accessor: "date" },
    { header: "Customer Name", accessor: "customerName" },
    { header: "Total Amount", accessor: "totalAmount" },
    { header: "Status", accessor: "status" },
  ];

  const rows = reportData?.salesAndSalesReturnConsolidateResponseList || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDownloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Download Report
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loading />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center">
          <FileText className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
          <p className="text-sm text-[#52627C]">No report data available</p>
        </div>
      ) : (
        <>
          <div className="rounded-[16px] border border-[#E8EEF9] overflow-hidden">
            <TableComponent
              caption="Consolidated sales and return report."
              columns={customerReportColumns}
              data={rows}
            />
          </div>
          <div className="flex justify-end">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerReportConsolidatedReport;
